import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const savedToken = localStorage.getItem('token');
            if (savedToken) {
                try {
                    const response = await axios.get(`${API_URL}/api/auth/me`, {
                        headers: { Authorization: `Bearer ${savedToken}` },
                        timeout: 10000 // 10 second timeout
                    });
                    setUser(response.data);
                    setToken(savedToken);
                } catch (error) {
                    console.error('Auth check failed:', error);
                    localStorage.removeItem('token');
                    setToken(null);
                    setUser(null);
                }
            }
            setLoading(false);
        };

        initAuth();
    }, []);

    const login = async (email, password, portal = null) => {
        const response = await axios.post(`${API_URL}/api/auth/login`, {
            email,
            password,
            ...(portal ? { portal } : {})
        }, { timeout: 15000 });
        
        // Check if 2FA is required
        if (response.data.requires_2fa) {
            return {
                requires_2fa: true,
                user_id: response.data.user_id,
                message: response.data.message
            };
        }
        
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const setTokenAndUser = (accessToken, userData) => {
        localStorage.setItem('token', accessToken);
        setToken(accessToken);
        setUser(userData);
    };

    const register = async (email, password, name, dateOfBirth = null, additionalData = {}) => {
        const response = await axios.post(`${API_URL}/api/auth/register`, {
            name,
            email,
            password,
            date_of_birth: dateOfBirth,
            phone: additionalData.phone,
            country: additionalData.country,
            trading_experience: additionalData.trading_experience,
            risk_tolerance: additionalData.risk_tolerance,
            preferred_cryptos: additionalData.preferred_cryptos,
            investment_goal: additionalData.investment_goal,
            referral_code: additionalData.referral_code
        }, { timeout: 15000 });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const registerRecovery = async (data) => {
        const response = await axios.post(`${API_URL}/api/auth/register`, {
            portal: 'recovery',
            name: data.name,
            email: data.email,
            password: data.password,
            phone: data.phone || null,
            country: data.country || null,
            loss_type: data.loss_type,
            loss_amount: data.loss_amount,
            loss_currency: data.loss_currency,
            loss_date: data.loss_date || null,
            platform_involved: data.platform_involved || null,
            loss_description: data.loss_description,
            has_evidence: data.has_evidence,
            reported_to_authorities: data.reported_to_authorities,
        }, { timeout: 15000 });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const updateUser = (updatedUserData) => {
        setUser(updatedUserData);
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        registerRecovery,
        logout,
        setTokenAndUser,
        updateUser,
        isAuthenticated: !!token,
        isRecoveryUser: user?.portal === 'recovery'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
